/**
 * IMAGE-CROP-UX-A1 — browser decode + canvas JPEG export for fixed avatars.
 * Inject decode/canvas boundaries for unit tests.
 */

import { getSourceCropRect } from "./avatarCropModel";
import {
  CROP_DECODE_ERROR_MESSAGE,
  CROP_DIMENSION_ERROR_MESSAGE,
  CROP_EXPORT_ERROR_MESSAGE,
} from "./avatarCropEligibility";

export const MAX_OUTPUT_SIDE = 512;
export const JPEG_QUALITY = 0.9;

export class AvatarCropError extends Error {
  constructor(code, userMessage) {
    super(userMessage);
    this.name = "AvatarCropError";
    this.code = code;
    this.userMessage = userMessage;
  }
}

/**
 * Remove only the final extension; append -cropped.jpg.
 * Examples:
 *   avatar.png → avatar-cropped.jpg
 *   photo.profile.JPG → photo.profile-cropped.jpg
 */
export function buildCroppedFilename(sourceName) {
  const fallback = "avatar-cropped.jpg";
  if (!sourceName || typeof sourceName !== "string") return fallback;
  const trimmed = sourceName.trim();
  if (!trimmed) return fallback;
  const slash = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  const base = slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
  if (!base || base === "." || base === "..") return fallback;
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const safe = stem.trim() || "avatar";
  return `${safe}-cropped.jpg`;
}

function defaultCreateImageBitmap(blob) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(blob);
  }
  return Promise.reject(new Error("createImageBitmap unavailable"));
}

function defaultLoadImageElement(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = url;
  });
}

async function defaultDecodeSource(file, deps) {
  const createObjectURL = deps.createObjectURL || URL.createObjectURL.bind(URL);
  const revokeObjectURL = deps.revokeObjectURL || URL.revokeObjectURL.bind(URL);
  const createImageBitmapFn = deps.createImageBitmap || defaultCreateImageBitmap;
  const loadImageElement = deps.loadImageElement || defaultLoadImageElement;

  // Prefer createImageBitmap (respects EXIF orientation in modern browsers).
  try {
    const bitmap = await createImageBitmapFn(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      drawSource: bitmap,
      cleanup: () => {
        if (typeof bitmap.close === "function") bitmap.close();
      },
    };
  } catch (_bitmapError) {
    // Fallback: HTMLImageElement via object URL.
    let objectUrl = null;
    try {
      objectUrl = createObjectURL(file);
      const img = await loadImageElement(objectUrl);
      return {
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        drawSource: img,
        cleanup: () => {
          if (objectUrl) revokeObjectURL(objectUrl);
        },
      };
    } catch (_imgError) {
      if (objectUrl) revokeObjectURL(objectUrl);
      throw new AvatarCropError("decode", CROP_DECODE_ERROR_MESSAGE);
    }
  }
}

function defaultCreateCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/**
 * Create a square JPEG File from the current crop transform.
 *
 * @param {File} file
 * @param {{ zoom: number, offsetX: number, offsetY: number, sourceWidth?: number, sourceHeight?: number, rotation?: number }} cropState
 * @param {number} viewportSize — editor viewport side used for the transform
 * @param {object} [deps] — injectable decode/canvas boundaries for tests
 */
export async function createCroppedAvatarFile(file, cropState, viewportSize, deps = {}) {
  if (!file) {
    throw new AvatarCropError("decode", CROP_DECODE_ERROR_MESSAGE);
  }
  if (!cropState || !Number.isFinite(viewportSize) || viewportSize <= 0) {
    throw new AvatarCropError("dimensions", CROP_DIMENSION_ERROR_MESSAGE);
  }

  const decodeSource = deps.decodeSource || defaultDecodeSource;
  let decoded;
  try {
    decoded = await decodeSource(file, deps);
  } catch (err) {
    if (err instanceof AvatarCropError) throw err;
    throw new AvatarCropError("decode", CROP_DECODE_ERROR_MESSAGE);
  }

  const rotation = normalizeRotation(cropState.rotation);
  let workingWidth = decoded.width;
  let workingHeight = decoded.height;
  let drawSource = decoded.drawSource;
  let rotatedCanvas = null;

  // Materialize a rotated working bitmap so crop geometry matches preview.
  if (rotation !== 0) {
    const createCanvas = deps.createCanvas || defaultCreateCanvas;
    const rw = rotation % 180 === 0 ? decoded.width : decoded.height;
    const rh = rotation % 180 === 0 ? decoded.height : decoded.width;
    rotatedCanvas = createCanvas(rw, rh);
    const rctx = rotatedCanvas.getContext("2d");
    if (!rctx) {
      if (decoded.cleanup) decoded.cleanup();
      throw new AvatarCropError("export", CROP_EXPORT_ERROR_MESSAGE);
    }
    rctx.fillStyle = "#ffffff";
    rctx.fillRect(0, 0, rw, rh);
    rctx.translate(rw / 2, rh / 2);
    rctx.rotate((rotation * Math.PI) / 180);
    rctx.drawImage(
      decoded.drawSource,
      -decoded.width / 2,
      -decoded.height / 2,
    );
    workingWidth = rw;
    workingHeight = rh;
    drawSource = rotatedCanvas;
  }

  const sourceWidth =
    cropState.sourceWidth > 0 ? cropState.sourceWidth : workingWidth;
  const sourceHeight =
    cropState.sourceHeight > 0 ? cropState.sourceHeight : workingHeight;
  if (!(sourceWidth > 0) || !(sourceHeight > 0)) {
    if (decoded.cleanup) decoded.cleanup();
    throw new AvatarCropError("dimensions", CROP_DIMENSION_ERROR_MESSAGE);
  }

  const rect = getSourceCropRect(
    sourceWidth,
    sourceHeight,
    viewportSize,
    cropState.zoom,
    cropState.offsetX,
    cropState.offsetY,
  );

  if (!rect || !(rect.width > 0) || !(rect.height > 0)) {
    if (decoded.cleanup) decoded.cleanup();
    throw new AvatarCropError("dimensions", CROP_DIMENSION_ERROR_MESSAGE);
  }

  const outputSide = Math.max(
    1,
    Math.floor(Math.min(MAX_OUTPUT_SIDE, rect.width, rect.height)),
  );

  const createCanvas = deps.createCanvas || defaultCreateCanvas;
  const canvas = createCanvas(outputSide, outputSide);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    if (decoded.cleanup) decoded.cleanup();
    throw new AvatarCropError("export", CROP_EXPORT_ERROR_MESSAGE);
  }

  // Transparent PNG/WebP/GIF → white fill (JPEG has no alpha).
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, outputSide, outputSide);

  try {
    ctx.drawImage(
      drawSource,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      0,
      0,
      outputSide,
      outputSide,
    );
  } catch (_drawError) {
    if (decoded.cleanup) decoded.cleanup();
    throw new AvatarCropError("export", CROP_EXPORT_ERROR_MESSAGE);
  }

  const quality = deps.jpegQuality != null ? deps.jpegQuality : JPEG_QUALITY;
  const toBlob =
    deps.toBlob ||
    ((c, type, q) => {
      if (typeof c.toBlob !== "function") {
        return Promise.reject(new Error("canvas.toBlob unavailable"));
      }
      return new Promise((resolve, reject) => {
        try {
          c.toBlob((result) => resolve(result), type, q);
        } catch (err) {
          reject(err);
        }
      });
    });
  const toDataURL =
    deps.toDataURL ||
    ((c, type, q) => {
      if (typeof c.toDataURL !== "function") {
        throw new Error("canvas.toDataURL unavailable");
      }
      return c.toDataURL(type, q);
    });
  const dataUrlToBlob =
    deps.dataUrlToBlob ||
    ((dataUrl) => {
      const match = /^data:([^;,]+)?(;base64)?,(.*)$/i.exec(dataUrl || "");
      if (!match) {
        throw new Error("invalid data URL");
      }
      const mime = match[1] || "image/jpeg";
      const isBase64 = Boolean(match[2]);
      const payload = match[3] || "";
      const binary = isBase64
        ? atob(payload)
        : decodeURIComponent(payload);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new Blob([bytes], { type: mime });
    });

  let blob = null;
  try {
    blob = await toBlob(canvas, "image/jpeg", quality);
  } catch (_blobError) {
    blob = null;
  }

  // Mobile Safari / capability gaps: toBlob may be absent, reject, or return null.
  // Fall back to toDataURL → Blob without introducing a new runtime library.
  if (!blob) {
    try {
      const dataUrl = toDataURL(canvas, "image/jpeg", quality);
      blob = dataUrlToBlob(dataUrl);
    } catch (_dataUrlError) {
      if (decoded.cleanup) decoded.cleanup();
      throw new AvatarCropError("export", CROP_EXPORT_ERROR_MESSAGE);
    }
  }

  if (decoded.cleanup) decoded.cleanup();

  if (!blob) {
    throw new AvatarCropError("export", CROP_EXPORT_ERROR_MESSAGE);
  }

  const filename = buildCroppedFilename(file.name);
  const FileCtor = deps.File || (typeof File !== "undefined" ? File : null);
  if (!FileCtor) {
    throw new AvatarCropError("export", CROP_EXPORT_ERROR_MESSAGE);
  }

  return new FileCtor([blob], filename, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

export function normalizeRotation(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const mod = ((Math.round(n) % 360) + 360) % 360;
  if (mod === 90 || mod === 180 || mod === 270) return mod;
  return 0;
}
